import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Divider,
  Snackbar,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import { Header } from 'components/Header';
import {
  BulkStatusUpdateRequest,
  ServiceRequestSummary,
} from '../services/types';
import {
  bulkStatusUpdate,
  searchServiceRequests,
  StatusIds,
} from '../services/api';
import { formatApiErrorMessage, logApiError } from '../services/errors';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';

import { Scanner as QrScannerComp } from '@yudiel/react-qr-scanner';
import { parseServiceList } from 'utils/orderMeta';

interface BulkFinishServicePageProps {
  externalIdentity: string;
  storeId: string;
  associateId: string;
  associateEmail: string;
  associateName?: string;
  profileId?: string;
}

type ScanType = 'order' | 'delivery' | 'general';

interface NormalizedScanResult {
  original: string;
  normalized: string;
  type: ScanType;
  queries: string[];
}

const PICKUP_STATUS = StatusIds.READY_TO_PICKUP;

const READY_FOR_FINISH_STATUS = StatusIds.WORK_IN_PROGRESS;

type FinishState = 'idle' | 'loading' | 'success' | 'error';

const normalizeScanInput = (raw: string): NormalizedScanResult | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const collapsed = trimmed.replace(/\s+/g, '');
  let normalized = collapsed;
  let type: ScanType = 'general';

  const explicitMatch =
    collapsed.match(/<open_logistic_order>(.*?)<\/open_logistic_order>/i) ||
    collapsed.match(/<open_logistic_order><([^>]+)>/i);
  if (explicitMatch && explicitMatch[1]) {
    normalized = explicitMatch[1].replace(/[<>]/g, '').trim();
    type = 'order';
  } else if (/open_logistic_order/i.test(collapsed)) {
    const inlineMatch = collapsed.match(/open_logistic_order([^<>\s]+)/i);
    if (inlineMatch && inlineMatch[1]) {
      normalized = inlineMatch[1].replace(/[^a-z0-9]/gi, '');
      type = 'order';
    }
  }

  if (
    /^[A-Za-z]{2}\d{4,}$/.test(normalized) ||
    /^RS\d+/i.test(normalized) ||
    /^SO\d+/i.test(normalized)
  ) {
    type = 'order';
  } else if (/^\d+$/.test(normalized)) {
    type = 'delivery';
  }

  const upper = /[a-z]/i.test(normalized)
    ? normalized.toUpperCase()
    : normalized;
  normalized = upper;

  const queries = new Set<string>();
  queries.add(normalized);
  if (normalized !== collapsed) queries.add(collapsed);
  if (normalized !== trimmed) queries.add(trimmed);

  return {
    original: trimmed,
    normalized,
    type,
    queries: Array.from(queries).filter(Boolean),
  };
};

const dedupeSummaries = (
  items: ServiceRequestSummary[]
): ServiceRequestSummary[] => {
  const map = new Map<string, ServiceRequestSummary>();
  items.forEach((item, idx) => {
    if (!item) return;
    const serviceItemId = ((item as any)?.serviceItemId || '').toString();
    const key =
      serviceItemId ||
      item.orderId?.toString?.() ||
      item.orderExternalId ||
      `idx-${idx}`;
    if (!map.has(key)) {
      map.set(key, item);
    }
  });
  return Array.from(map.values());
};

const buildBulkFinishPayload = (
  items: ServiceRequestSummary[],
  associateId: string,
  associateEmail: string,
  associateName?: string
): BulkStatusUpdateRequest => {
  const serviceItems = items
    .filter((item) => item.serviceItemId)
    .map((item) => ({
      serviceItemId: item.serviceItemId as string,
      serviceGuids: parseServiceList(item.services)
        .map((svc: any) => svc?.service_guid)
        .filter(Boolean),
    }));

  return {
    status: PICKUP_STATUS,
    associateId,
    associateName: associateName?.trim() || associateEmail || associateId,
    associateEmail,
    serviceItems,
    requestType:"READY"
  };
};

const getServiceItemId = (item: ServiceRequestSummary): string => {
  return ((item as any)?.serviceItemId || '').toString();
};

const getOrderKey = (item: ServiceRequestSummary): string => {
  const serviceItemId = getServiceItemId(item);
  const orderId = (item as any)?.orderId;
  return item.orderExternalId || (orderId ? String(orderId) : serviceItemId);
};

const isReadyForFinsh = (item: ServiceRequestSummary): boolean => {
  const raw = (item as any)?.statusId;
  const numeric = Number(raw);
  return !Number.isNaN(numeric) && numeric === READY_FOR_FINISH_STATUS;
};

const getServiceItemCount = (items: ServiceRequestSummary[]) => {
  const uniqueIds = new Set(
    items.map((item) => item.serviceItemId).filter(Boolean)
  );

  return uniqueIds.size;
};

const BulkFinishServicePage: React.FC<BulkFinishServicePageProps> = ({
  externalIdentity,
  storeId,
  associateId,
  associateEmail,
  associateName,
}) => {
  const navigate = useNavigate();
  const findScannerContainerRef = useRef<HTMLDivElement | null>(null);

  const isSecureContext =
    typeof window !== 'undefined' &&
    (window.isSecureContext ||
      ['localhost', '127.0.0.1'].includes(window.location.hostname));

  const [findInput, setFindInput] = useState('');
  const [findResults, setFindResults] = useState<ServiceRequestSummary[]>([]);
  const [findLoading, setFindLoading] = useState(false);
  const [findError, setFindError] = useState<string | null>(null);
  const [findScanning, setFindScanning] = useState(true);
  const [findScannerKey, setFindScannerKey] = useState(0);
  const [findCameraHint, setFindCameraHint] = useState<string | null>(null);
  const [lastScanSignature, setLastScanSignature] = useState<string | null>(
    null
  );
  const [finishState, setFinishState] = useState<Record<string, FinishState>>(
    {}
  );
  const [allFinished, setAllFinished] = useState(false);
  const [bulkAllLoading, setBulkAllLoading] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);
  // Guard against stale async responses updating state
  const [failedServiceItems, setFailedServiceItems] = useState<string[]>([]);
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>(
    {}
  );
  const [deleteOrderId, setDeleteOrderId] = useState<string | null>(null);
  const [limitDialog, setLimitDialog] = useState<{
    open: boolean;
    message: string;
  }>({
    open: false,
    message: '',
  });
  const scanLockRef = useRef(false);
  const resolvedAssociateName = useMemo(
    () => associateName?.trim() || associateEmail || associateId,
    [associateId, associateEmail, associateName]
  );

  const groupedFindResults = useMemo(() => {
    if (!findResults.length)
      return [] as {
        key: string;
        orderExternalId: string;
        items: ServiceRequestSummary[];
      }[];
    const map = new Map<string, ServiceRequestSummary[]>();
    findResults.forEach((item) => {
      if (!item) return;
      const key = getOrderKey(item) || `row-${map.size}`;
      const existing = map.get(key);
      if (existing) existing.push(item);
      else map.set(key, [item]);
    });
    return Array.from(map.entries()).map(([key, items]) => ({
      key,
      orderExternalId: items[0]?.orderExternalId || key,
      items,
    }));
  }, [findResults]);

  const readyFinishItems = useMemo(
    () => findResults.filter(isReadyForFinsh),
    [findResults]
  );

  const runFindLookup = useCallback(
    async (target: NormalizedScanResult) => {
      setFindLoading(true);
      setFindError(null);
      try {
        const aggregated: ServiceRequestSummary[] = [];
        const querySet = new Set<string>(
          target.queries.map((q) => q.trim()).filter(Boolean)
        );
        if (!querySet.size) {
          querySet.add(target.normalized);
        }

        for (const q of querySet) {
          const trimmed = q.trim();
          if (!trimmed) continue;
          const matches = await searchServiceRequests(
            externalIdentity,
            '4', //filter only work in progress
            storeId,
            trimmed,
            'Store',
            undefined,
            '100'
          );
          if (Array.isArray(matches)) {
            aggregated.push(...matches);
          }
        }

        const deduped = dedupeSummaries(aggregated);
        setFinishState({});
        setBulkAllLoading(false);
        if (!deduped.length) {
          setFindError(
            'No matching orders found, or the order is not in Work In Progress status.'
          );
          return;
        }
        setFindResults((prev) => {
          const existingIds = new Set(prev.map((p) => p.serviceItemId));

          const newItems = deduped.filter(
            (d) => !existingIds.has(d.serviceItemId)
          );
          if (!newItems.length) {
            setSnack('Order already added.');
            return prev;
          }
          // get order id
          const orderIds = Array.from(
            new Set(
              newItems.map((item) => item.orderExternalId || item.orderId || '')
            )
          ).filter(Boolean);
          const orderIDsText = orderIds?.join(', ');
          const currentCount = getServiceItemCount(prev);
          const newCount = newItems?.length;
          if (currentCount + newCount > 30) {
            setLimitDialog({
              open: true,
              message: `Cannot add order ${orderIDsText}. Adding another ${newCount} service items will exceeds the maximum allowed limit of 30 service item per request.`,
            });
            return prev;
          }

          return [...prev, ...newItems];
        });

        if (!deduped.length) {
          setFindError(
            'No matching orders found, or the order is not in Work In Progress status.'
          );
        }
      } catch (err) {
        logApiError('Find Order lookup failed', err);
        setFindResults([]);
        setFindError(
          formatApiErrorMessage(
            err,
            'Unable to locate an order with that value.'
          )
        );
      } finally {
        setFindLoading(false);
      }
    },
    [externalIdentity, storeId]
  );

  const handleFindSubmit = useCallback(() => {
    const value = findInput.trim();
    if (!value) {
      setFindError('Enter an order number, tag ID.');
      return;
    }
    const normalized = normalizeScanInput(value) || {
      original: value,
      normalized: /[a-z]/i.test(value) ? value.toUpperCase() : value,
      type: /^\d+$/.test(value) ? ('delivery' as ScanType) : 'general',
      queries: [value],
    };
    setFindCameraHint(null);
    setFindScanning(false);
    setLastScanSignature(null);
    runFindLookup(normalized);
    // setFindInput('');
  }, [findInput, runFindLookup]);

  const handleFindClear = useCallback(() => {
    setFindInput('');
    setFindResults([]);
    setFailedServiceItems([]);
    setFindError(null);
    setLastScanSignature(null);
    setFinishState({});
    setBulkAllLoading(false);
    setExpandedOrders({});
    if (isSecureContext) {
      setFindScanning(true);
      setFindScannerKey((k) => k + 1);
    }
  }, [isSecureContext]);

  const handleScanResult = useCallback(
    (raw: string) => {
      if (scanLockRef.current) return;
      scanLockRef.current = true;
      const normalized = normalizeScanInput(raw);
      if (!normalized) return;
      const signature = `${normalized.type}:${normalized.normalized}`;
      if (signature === lastScanSignature) return;
      setLastScanSignature(signature);
      setFindInput(normalized.normalized);
      setFindCameraHint(null);
      setFindScanning(false);
      setTimeout(() => {
        runFindLookup(normalized);
        setFindInput('');
        scanLockRef.current = false;
      }, 100);
      // runFindLookup(normalized);
      // setFindInput('');
    },
    [lastScanSignature, runFindLookup]
  );

  const handleBulkAllFinish = useCallback(async () => {
    if (!readyFinishItems.length) {
      setSnack('No ready items to finish service.');
      return;
    }

    setBulkAllLoading(true);
    const payload = buildBulkFinishPayload(
      readyFinishItems,
      associateId,
      associateEmail,
      resolvedAssociateName
    );

    try {
      const response = await bulkStatusUpdate(externalIdentity, payload);
      const resultsMap = new Map(
        response?.results.map((r) => [r.serviceItemId, r])
      );

      let succeeded = 0;
      let failed = 0;
      const failedIds: string[] = [];

      setFindResults((prev) =>
        prev.map((item: any) => {
          const result = resultsMap?.get(item.serviceItemId);
          if (!result) return item;

          if (result.status === 'FAILED') {
            failed++;
            failedIds.push(result.serviceItemId);
            setFinishState((prevState) => ({
              ...prevState,
              [item.serviceItemId]: 'error',
            }));

            return item;
          }

          // SUCCESS case
          succeeded++;

          setFinishState((prevState) => ({
            ...prevState,
            [item.serviceItemId]: 'success',
          }));
          return {
            ...item,
            statusId: PICKUP_STATUS,
          };
        })
      );

      if (failed > 0) {
        setFailedServiceItems(failedIds);
        setSnack(`${succeeded} finished, ${failed} failed.`);
        setAllFinished(false);
      } else {
        setFailedServiceItems([]);
        setSnack('All services finished successfully.');
        setAllFinished(true);
      }
    } catch (err) {
      setSnack(
        formatApiErrorMessage(err, 'Bulk finish failed. Please try again.')
      );
    } finally {
      setBulkAllLoading(false);
    }
  }, [
    readyFinishItems,
    associateId,
    associateEmail,
    resolvedAssociateName,
    externalIdentity,
    setSnack,
  ]);
  const handleRetryFailedItems = async () => {
    if (!failedServiceItems.length) return;

    const retryItems = readyFinishItems.filter((item: any) =>
      failedServiceItems.includes(item.serviceItemId)
    );

    if (!retryItems.length) {
      setSnack('No failed items to retry.');
      return;
    }

    setBulkAllLoading(true);

    const payload = buildBulkFinishPayload(
      retryItems,
      associateId,
      associateEmail,
      resolvedAssociateName
    );

    try {
      await bulkStatusUpdate(externalIdentity, payload);
      setSnack('Retry completed.');
    } catch (err) {
      setSnack(formatApiErrorMessage(err, 'Retry failed. Please try again.'));
    } finally {
      setBulkAllLoading(false);
    }
  };
  useEffect(() => {
    if (!findScanning) return;
    const node = findScannerContainerRef.current;
    if (!node) return;
    const video: HTMLVideoElement | null = node.querySelector('video');
    if (!video) return;

    const handleEnded = () => {
      setFindCameraHint('Camera closed. Tap Scan Again to continue.');
      setFindScanning(false);
    };

    video.addEventListener('pause', handleEnded);
    video.addEventListener('ended', handleEnded);
    (video as any).addEventListener?.('webkitendfullscreen', handleEnded);

    return () => {
      video.removeEventListener('pause', handleEnded);
      video.removeEventListener('ended', handleEnded);
      (video as any).removeEventListener?.('webkitendfullscreen', handleEnded);
    };
  }, [findScanning]);

  useEffect(() => {
    if (allFinished) {
      setFindScanning(false);
    }
  }, [allFinished]);

  const removeItemFromList = useCallback((orderExternalId: string) => {
    setFindResults((prev) =>
      prev.filter((item) => item.orderExternalId !== orderExternalId)
    );
  }, []);

  const handleOpenDeleteDialog = (orderId: string) => {
    setDeleteOrderId(orderId);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteOrderId(null);
  };

  const handleConfirmDelete = () => {
    if (deleteOrderId) {
      removeItemFromList(deleteOrderId);
    }
    setDeleteOrderId(null);
  };
  const toggleOrder = (key: string) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header
        title="Bulk Finish Service"
        showBackButton
        onBack={() => navigate(-1)}
        sticky
        elevateOnScroll
        align="left"
      />

      <Paper
        elevation={0}
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          p: 2,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          background: alpha(theme.palette.primary.light, 0.06),
        })}
      >
        <Stack spacing={2}>
          <Stack spacing={1.5}>
            <Typography variant="body1" color="text.primary">
              Find Orders to Finish Service
            </Typography>

            <Stack spacing={2}>
              {isSecureContext ? (
                findScanning ? (
                  <Box
                    ref={findScannerContainerRef}
                    sx={{ borderRadius: 2, overflow: 'hidden' }}
                  >
                    <QrScannerComp
                      key={findScannerKey}
                      onScan={(codes: any[]) => {
                        const text =
                          Array.isArray(codes) && codes[0]?.rawValue
                            ? codes[0].rawValue
                            : codes?.[0]?.rawValue ||
                              codes?.[0]?.rawValueText ||
                              '';
                        if (!text) return;
                        handleScanResult(text);
                        setFindScanning(false);
                      }}
                      onError={(err: any) => {
                        console.error(err);
                        setFindCameraHint(
                          'Camera error. Tap Scan Again to retry.'
                        );
                        setFindScanning(false);
                      }}
                      constraints={{ facingMode: 'environment' }}
                      styles={{ video: { width: '100%' } }}
                      formats={[
                        'qr_code',
                        'code_128',
                        'code_39',
                        'ean_13',
                        'ean_8',
                        'pdf417',
                      ]}
                    />
                  </Box>
                ) : (
                  <Stack spacing={1} alignItems="stretch">
                    <Button
                      variant="outlined"
                      onClick={() => {
                        scanLockRef.current = false;
                        setFindScanning(true);
                        setFindScannerKey((k) => k + 1);
                        setFindCameraHint(null);
                        setLastScanSignature(null);
                      }}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 700,
                      }}
                      disabled={allFinished}
                    >
                      Scan Again
                    </Button>
                    {findCameraHint && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: 'center' }}
                      >
                        {findCameraHint}
                      </Typography>
                    )}
                  </Stack>
                )
              ) : (
                <Alert severity="info" variant="outlined">
                  Camera access requires HTTPS or localhost. Use manual entry
                  below.
                </Alert>
              )}

              <Divider textAlign="center">or</Divider>

              <TextField
                label="Order ID"
                value={findInput}
                onChange={(e) => setFindInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleFindSubmit();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: findInput ? (
                    <InputAdornment position="end">
                      <Tooltip title="Clear">
                        <IconButton size="small" onClick={handleFindClear}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : undefined,
                }}
              />

              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleFindSubmit}
                  disabled={!findInput.trim() || findLoading || allFinished}
                  sx={(theme) => ({
                    flex: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    '&.Mui-disabled': {
                      color: theme.palette.text.primary,
                      opacity: 0.6,
                    },
                  })}
                >
                  Add Order
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleFindClear}
                  disabled={findLoading && !findResults.length && !findInput}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                  }}
                >
                  Reset
                </Button>
              </Stack>

              {findLoading && <LinearProgress sx={{ borderRadius: 2 }} />}

              {findError && (
                <Alert severity="warning" variant="outlined">
                  {findError}
                </Alert>
              )}

              {!findLoading && !findError && !findResults.length && (
                <Typography variant="body2" color="text.secondary">
                  Scan or search to view order matches.
                </Typography>
              )}

              {!findLoading && !findError && readyFinishItems?.length > 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                  Only service items in Work In Progress status are shown below
                  for these order(s).
                </Typography>
              )}

              {!findLoading && groupedFindResults?.length > 0 && (
                <Stack spacing={1.5}>
                  {groupedFindResults.map(({ key, orderExternalId, items }) => {
                    const isExpanded = expandedOrders[key];
                    const successCount = items.filter(
                      (i) => finishState[getServiceItemId(i)] === 'success'
                    ).length;

                    const errorCount = items.filter(
                      (i) => finishState[getServiceItemId(i)] === 'error'
                    ).length;

                    const allSuccess =
                      items?.length > 0 &&
                      items?.every(
                        (i) => finishState[getServiceItemId(i)] === 'success'
                      );
                    return (
                      <Paper
                        key={key}
                        variant="outlined"
                        onClick={() => toggleOrder(key)}
                        sx={(theme) => ({
                          p: 1.5,
                          borderRadius: 2,
                          borderColor: alpha(theme.palette.divider, 0.75),
                          backgroundColor: alpha(
                            theme.palette.background.paper,
                            0.7
                          ),
                          cursor: 'pointer',
                        })}
                      >
                        <Stack spacing={1.25}>
                          {/* Row 1 - Header */}
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                            sx={{ mb: 0.5 }}
                          >
                            <Typography variant="subtitle1" fontWeight={700}>
                              Order {orderExternalId || '—'}
                            </Typography>

                            <Stack
                              direction="row"
                              spacing={{ xs: 2, sm: 1 }}
                              alignItems="center"
                              justifyContent={{ xs: 'space-between' }}
                            >
                              {/* Item count */}
                              <Chip
                                label={`${items.length} Item${items.length > 0 ? 's' : ''}`}
                                size="small"
                                variant="outlined"
                                sx={{ borderRadius: 1, fontWeight: 600 }}
                              />

                              {/* WorkInProgress chip */}
                              <Chip
                                size="small"
                                label={
                                  allSuccess
                                    ? 'Ready for Pickup'
                                    : 'Work In Progress'
                                }
                                sx={(theme) => ({
                                  borderRadius: 1,
                                  fontWeight: 600,
                                  bgcolor: allSuccess
                                    ? alpha(theme.palette.success.main, 0.15)
                                    : alpha(theme.palette.info.main, 0.15),
                                  color: allSuccess
                                    ? theme.palette.success.main
                                    : theme.palette.info.main,
                                })}
                              />

                              {/* Expand icon */}
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                {isExpanded ? (
                                  <ExpandLessIcon fontSize="medium" />
                                ) : (
                                  <ExpandMoreIcon fontSize="medium" />
                                )}

                                <IconButton
                                  size="small"
                                  aria-label="Remove item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDeleteDialog(orderExternalId);
                                  }}
                                  sx={{
                                    ml: { xs: '16px', sm: 1 },
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Stack>
                          </Stack>

                          {/* COLLAPSED VIEW */}
                          {!isExpanded && (
                            <>
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="space-between"
                              >
                                {/* Row 2 - SKU */}
                                <Stack>
                                  <Typography variant="body2" fontWeight={600}>
                                    SKU: {items[0]?.sku || '—'} ({items.length})
                                  </Typography>

                                  {/* Row 3 - Tags */}
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Tags:{' '}
                                    {items
                                      .slice(0, 3)
                                      .map((i) => i.tag)
                                      .join(', ')}
                                    {items.length > 3 && (
                                      <Typography
                                        component="span"
                                        variant="caption"
                                        color="text.disabled"
                                        sx={{ ml: 0.5 }}
                                      >
                                        +{items.length - 3} more
                                      </Typography>
                                    )}
                                  </Typography>
                                </Stack>
                                {!isExpanded && (
                                  <Stack>
                                    {successCount > 0 && (
                                      <Chip
                                        icon={
                                          <CheckCircleOutlineIcon fontSize="small" />
                                        }
                                        label={`Finished Service for ${successCount} item${successCount > 1 ? 's' : ''}`}
                                        sx={(theme) => ({
                                          borderRadius: 2,
                                          fontWeight: 600,
                                          bgcolor: alpha(
                                            theme.palette.success.main,
                                            0.15
                                          ),
                                          color: theme.palette.success.main,
                                          mb: 0.5,
                                        })}
                                      />
                                    )}

                                    {errorCount > 0 && (
                                      <Chip
                                        icon={
                                          <ErrorOutlineIcon fontSize="small" />
                                        }
                                        label={`${errorCount} item${errorCount > 1 ? 's' : ''} failed`}
                                        sx={(theme) => ({
                                          borderRadius: 2,
                                          fontWeight: 600,
                                          bgcolor: alpha(
                                            theme.palette.error.main,
                                            0.15
                                          ),
                                          color: theme.palette.error.main,
                                        })}
                                      />
                                    )}
                                  </Stack>
                                )}
                              </Stack>
                            </>
                          )}

                          {/* EXPANDED VIEW */}
                          {isExpanded && (
                            <Stack spacing={1}>
                              {items.map((item, idx) => {
                                const serviceItemId = getServiceItemId(item);
                                const itemKey = serviceItemId
                                  ? `${serviceItemId}-${idx}`
                                  : `${key}-${idx}`;

                                const state = finishState[serviceItemId];

                                return (
                                  <React.Fragment key={itemKey}>
                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      sx={{
                                        px: 1,
                                        py: 0.75,
                                        borderRadius: 1,
                                        backgroundColor: (theme) =>
                                          alpha(
                                            theme.palette.primary.light,
                                            0.06
                                          ),
                                      }}
                                    >
                                      {/* Tag */}
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                      >
                                        Tag: {item.tag || '—'}
                                      </Typography>

                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                      >
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigate('/order-details', {
                                              state: item,
                                            });
                                          }}
                                          sx={{
                                            textTransform: 'none',
                                            borderRadius: 2,
                                            fontWeight: 600,
                                          }}
                                        >
                                          View Details
                                        </Button>

                                        {/* Success */}
                                        {state === 'success' && (
                                          <Chip
                                            icon={
                                              <CheckCircleOutlineIcon fontSize="small" />
                                            }
                                            label="Finished"
                                            size="small"
                                            sx={(theme) => ({
                                              borderRadius: 1,
                                              fontWeight: 600,
                                              bgcolor: alpha(
                                                theme.palette.success.main,
                                                0.15
                                              ),
                                              color: theme.palette.success.main,
                                            })}
                                          />
                                        )}

                                        {/* Error */}
                                        {state === 'error' && (
                                          <Chip
                                            icon={
                                              <ErrorOutlineIcon fontSize="small" />
                                            }
                                            label="Failed"
                                            size="small"
                                            sx={(theme) => ({
                                              borderRadius: 1,
                                              fontWeight: 600,
                                              bgcolor: alpha(
                                                theme.palette.error.main,
                                                0.15
                                              ),
                                              color: theme.palette.error.main,
                                            })}
                                          />
                                        )}
                                      </Stack>
                                    </Stack>

                                    {/* Divider*/}
                                    {idx !== items.length - 1 && <Divider />}
                                  </React.Fragment>
                                );
                              })}
                            </Stack>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              )}

              {allFinished && !findLoading && (
                <Stack direction="row" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setFindResults([]);
                      setFinishState({});
                      setFailedServiceItems([]);
                      setAllFinished(false);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 2,
                    }}
                  >
                    Close
                  </Button>
                </Stack>
              )}

              {readyFinishItems?.length > 0 && !findLoading && (
                <LoadingButton
                  variant="contained"
                  color="secondary"
                  onClick={handleBulkAllFinish}
                  loading={bulkAllLoading}
                  loadingIndicator={
                    <CircularProgress size={16} color="inherit" />
                  }
                  disabled={bulkAllLoading || failedServiceItems.length > 0}
                  sx={(theme) => ({
                    textTransform: 'none',
                    fontWeight: 700,
                    '& .MuiCircularProgress-root': {
                      color: theme.palette.common.white,
                    },
                  })}
                >
                  {`Confirm Finish Service of ${readyFinishItems.length > 1 ? 'All' : ''} ${readyFinishItems.length} Items`}
                </LoadingButton>
              )}

              {failedServiceItems?.length > 0 && !findLoading && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleRetryFailedItems}
                  sx={{ mt: 2, textTransform: 'none', fontWeight: 700 }}
                >
                  Retry {failedServiceItems.length} Failed Item
                  {failedServiceItems.length > 1 ? 's' : ''}
                </Button>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
      />
      {/* Delete confirmation box */}

      <Dialog open={!!deleteOrderId} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Remove Order?</DialogTitle>

        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to remove this order from the list?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>

          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* max limit dialog box */}
      <Dialog
        open={limitDialog.open}
        onClose={() => setLimitDialog({ open: false, message: '' })}
      >
        <DialogTitle>Bulk Finish Service Limit Exceeded</DialogTitle>

        <DialogContent>
          <Typography variant="body2">{limitDialog.message}</Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'flex-end', pr: 2, pb: 2 }}>
          <Button
            onClick={() => setLimitDialog({ open: false, message: '' })}
            sx={{ textTransform: 'none', fontWeight: 700 }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkFinishServicePage;
